import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Menu as MenuIcon, ChevronLeft, ChevronRight, InfoOutlined } from '@mui/icons-material';
import { useThemeMode, vortxColors, retroColors } from '@vortx/shared-ui';

/**
 * Floating edge tab for triggering responsive drawers
 *
 * @param {string} anchor - 'left' or 'right'
 * @param {boolean} open - Whether the drawer is open
 * @param {function} onClick - Toggle handler
 */
const DrawerEdgeTab = ({ anchor, open, onClick }) => {
    const { isRetro } = useThemeMode();

    // Icon logic
    const getIcon = () => {
        if (anchor === 'left') {
            return open ? <ChevronLeft /> : <MenuIcon />;
        }
        return open ? <ChevronRight /> : <InfoOutlined />;
    };

    // Theme-aware styles
    const lightStyles = {
        background: open ? alpha(vortxColors.gold, 0.1) : vortxColors.gray,
        border: `1px solid ${alpha(vortxColors.teal, 0.3)}`,
        color: open ? vortxColors.teal : vortxColors.stone,
        '&:hover': {
            background: alpha(vortxColors.gold, 0.1),
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
    };

    const retroStyles = {
        background: 'rgba(0, 40, 40, 0.9)',
        border: `1px solid ${retroColors.accent}`,
        boxShadow: open
            ? `0 0 12px ${alpha(retroColors.accent, 0.5)}`
            : `0 0 8px ${alpha(retroColors.accent, 0.3)}`,
        color: retroColors.accent,
        '&:hover': {
            boxShadow: `0 0 12px ${alpha(retroColors.accent, 0.5)}`,
        },
    };

    const styles = isRetro ? retroStyles : lightStyles;

    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'fixed',
                top: '50%',
                transform: 'translateY(-50%)',
                [anchor]: 0,
                zIndex: 1100,
                width: 24,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: anchor === 'left'
                    ? '0 8px 8px 0'
                    : '8px 0 0 8px',
                transition: 'all 0.2s ease',
                ...styles,
                // Remove border on edge side
                [anchor === 'left' ? 'borderLeft' : 'borderRight']: 'none',
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
            aria-label={`${open ? 'Close' : 'Open'} ${anchor} sidebar`}
        >
            {getIcon()}
        </Box>
    );
};

export default DrawerEdgeTab;
